import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { salary as salaryService } from "services/api/company/salary.service";
import { organizationalStructure as organizationalStructureService } from "services/api/company/organizationalStructure.service";
import type { CompanyWorker } from "model/Company";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";

interface PersonOption {
  value: string;
  label: string;
}

interface OrganizationalStructureListItem {
  companyUnitId: number;
  companyUnitName: string;
  workGroupId: number;
  workGroupName: string;
}

interface UseCompanyFilterOptionsParams {
  companyId: number | undefined;
  setSelectedWorkGroupIds?: Dispatch<SetStateAction<string[]>>;
}

interface UseCompanyFilterOptionsState {
  unitTeamOptions: UnitTeamOption[];
  personOptions: PersonOption[];
  workersLoadError: boolean;
  unitTeamLoadError: boolean;
}

const personOptionsCache = new Map<number, PersonOption[]>();
const personOptionsInFlight = new Map<
  number,
  Promise<{ options: PersonOption[]; hasError: boolean }>
>();
const unitTeamOptionsCache = new Map<number, UnitTeamOption[]>();
const unitTeamOptionsInFlight = new Map<
  number,
  Promise<{ options: UnitTeamOption[]; hasError: boolean }>
>();

const buildUnitTeamOptions = (
  items: OrganizationalStructureListItem[],
): UnitTeamOption[] => {
  const units: UnitTeamOption[] = [];
  const unitIndex = new Map<number, UnitTeamOption>();
  items.forEach((item) => {
    let unit = unitIndex.get(item.companyUnitId);
    if (!unit) {
      unit = {
        unitId: item.companyUnitId,
        unitName: item.companyUnitName,
        teams: [],
      };
      unitIndex.set(item.companyUnitId, unit);
      units.push(unit);
    }
    if (!unit.teams.some((team) => team.id === item.workGroupId)) {
      unit.teams.push({
        id: item.workGroupId,
        name: item.workGroupName,
      });
    }
  });
  return units;
};

const toPersonOptions = (workers: CompanyWorker[]): PersonOption[] => {
  return workers.map((worker) => {
    const label = `${worker.firstName ?? ""} ${worker.lastName ?? ""}`.trim();
    return {
      value: String(worker.id),
      label: label.length > 0 ? label : String(worker.id),
    };
  });
};

const loadPersonOptions = async (
  companyId: number,
): Promise<{ options: PersonOption[]; hasError: boolean }> => {
  const cached = personOptionsCache.get(companyId);
  if (cached) {
    return { options: cached, hasError: false };
  }

  const inFlight = personOptionsInFlight.get(companyId);
  if (inFlight) {
    return inFlight;
  }

  const request = salaryService
    .getCompanyWorkers(companyId, false)
    .then((response) => {
      const mappedOptions = toPersonOptions(response.data);
      personOptionsCache.set(companyId, mappedOptions);
      return { options: mappedOptions, hasError: false };
    })
    .catch(() => ({ options: [], hasError: true }))
    .finally(() => {
      personOptionsInFlight.delete(companyId);
    });

  personOptionsInFlight.set(companyId, request);
  return request;
};

const loadUnitTeamOptions = async (
  companyId: number,
): Promise<{ options: UnitTeamOption[]; hasError: boolean }> => {
  const cached = unitTeamOptionsCache.get(companyId);
  if (cached) {
    return { options: cached, hasError: false };
  }

  const inFlight = unitTeamOptionsInFlight.get(companyId);
  if (inFlight) {
    return inFlight;
  }

  const request = organizationalStructureService
    .getOrganizationalStructureList(companyId)
    .then((response) => {
      const mappedOptions = buildUnitTeamOptions(response.data);
      unitTeamOptionsCache.set(companyId, mappedOptions);
      return { options: mappedOptions, hasError: false };
    })
    .catch(() => ({ options: [], hasError: true }))
    .finally(() => {
      unitTeamOptionsInFlight.delete(companyId);
    });

  unitTeamOptionsInFlight.set(companyId, request);
  return request;
};

/**
 * Loads and maps company filter data used by toolbar controls.
 * Returns unit/team options and person options for the provided company.
 * Optionally keeps selected work-group ids in sync with currently available teams.
 */
export const useCompanyFilterOptions = (
  params: UseCompanyFilterOptionsParams,
): UseCompanyFilterOptionsState => {
  const companyId = params.companyId;
  const setSelectedWorkGroupIds = params.setSelectedWorkGroupIds;
  const [unitTeamOptions, setUnitTeamOptions] = useState<UnitTeamOption[]>([]);
  const [personOptions, setPersonOptions] = useState<PersonOption[]>([]);
  const [workersLoadError, setWorkersLoadError] = useState(false);
  const [unitTeamLoadError, setUnitTeamLoadError] = useState(false);

  useEffect(() => {
    if (!companyId) {
      setPersonOptions([]);
      setWorkersLoadError(false);
      return;
    }
    let isMounted = true;
    void loadPersonOptions(companyId).then(({ options, hasError }) => {
      if (!isMounted) return;
      setPersonOptions(options);
      setWorkersLoadError(hasError);
    });
    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setUnitTeamOptions([]);
      setUnitTeamLoadError(false);
      return;
    }
    let isMounted = true;
    void loadUnitTeamOptions(companyId).then(({ options, hasError }) => {
      if (!isMounted) return;
      setUnitTeamOptions(options);
      setUnitTeamLoadError(hasError);
    });
    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!setSelectedWorkGroupIds || unitTeamOptions.length === 0) return;
    const availableIds = new Set(
      unitTeamOptions.flatMap((unit) =>
        unit.teams.map((team) => String(team.id)),
      ),
    );
    setSelectedWorkGroupIds((prev) =>
      prev.filter((value) => availableIds.has(value)),
    );
  }, [setSelectedWorkGroupIds, unitTeamOptions]);

  return {
    unitTeamOptions,
    personOptions,
    workersLoadError,
    unitTeamLoadError,
  };
};
